import React, { useState, useContext, useEffect } from 'react';
import { useDataLoader } from '../common/hooks';
import { PreferenceContext, UserContext } from '../context';
import { getCommentsData, getModeratorCommentsData } from 'services/comments';
import { commentsSortings } from '../../data/sortings';

export const CommentsContext = React.createContext({});
CommentsContext.displayName = 'Comments';

const initialCommentsState = {
  summary: {
    can_comment: undefined,
    moderated_at: undefined,
  },
  authors: [],
  comments: [],
};

const commentsWithUsername = (comments, authors) => {
  return comments
    ? [
        ...comments.map((comment) => ({
          ...comment,
          username: comment.username
            ? comment.username
            : authors.find((author) => author.id === comment.author).username,
        })),
      ]
    : [];
};

const createNewCommentsList = (comments, authors, sorting) => {
  let commentsList = [];
  const topComment = comments ? comments.find((comment) => comment.top_comment) : false;
  if (topComment) {
    commentsList = [
      topComment,
      ...commentsSortings(
        commentsWithUsername(comments, authors).filter((comment) => comment.id !== topComment.id),
        sorting,
      ),
    ];
  } else {
    commentsList = commentsSortings(commentsWithUsername(comments, authors), sorting);
  }
  return commentsList;
};

export const CommentsContextProvider = ({ itemId, item, children }) => {
  const {
    preference: { sorting: { comments: commentsSort } = {} },
  } = useContext(PreferenceContext);

  const { user } = useContext(UserContext);

  const loadCommentsList = useDataLoader(
    user && user.moderate ? getModeratorCommentsData : getCommentsData,
  );
  const commentsLoaded = loadCommentsList({ itemId: itemId.replace('_', '/') });
  const [error, setError] = useState(false);
  const [commentsData, setCommentsData] = useState(initialCommentsState);
  const [moderatedTime, setModeratedTime] = useState(undefined);
  const [lastAddedComment, setLastAddedComment] = useState('');
  const userCanModerate = user && user.moderate;
  const [showAllComments, setShowAllComments] = useState(
    (commentsSort === 'Moderator view' && userCanModerate) || false,
  );
  const {
    authors,
    comments,
    summary: { can_comment: canComment },
  } = commentsData;

  const setData = () => {
    const { result, error } = commentsLoaded;
    if (error) {
      setError(error);
    } else {
      setCommentsData({
        ...result.data,
        comments: createNewCommentsList(result.data.comments, result.data.authors, commentsSort),
      });
      setModeratedTime(result.data.summary.moderated_at);
    }
  };

  const setCommentsList = (comments) => {
    setCommentsData({
      ...commentsData,
      comments: createNewCommentsList(comments, authors, commentsSort),
    });
  };

  useEffect(() => {
    setData();
  }, []);

  useEffect(() => {
    setData();
  }, [user, itemId]);

  useEffect(() => {
    if (commentsData.comments.length > 0) {
      setCommentsData({
        ...commentsData,
        comments: createNewCommentsList(commentsData.comments, authors, commentsSort),
      });
    }
  }, [commentsSort]);

  return (
    <CommentsContext.Provider
      value={{
        item,
        itemId,
        authorsList: authors,
        openComments: canComment,
        commentsList: comments,
        commentsData,
        setCommentsList,
        commentsSort,
        commentsSortings,
        lastAddedComment,
        setLastAddedComment,
        moderatedTime,
        setModeratedTime,
        showAllComments,
        setShowAllComments,
      }}
    >
      {error && <div>Er gaat iets mis...</div>}
      {!error && <>{children}</>}
    </CommentsContext.Provider>
  );
};
